import { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { BudgetAttachmentCard } from "./BudgetAttachmentCard";
import { IBudgetAttachmentFile } from "./types";
import {
  ALLOWED_BUDGET_ATTACHMENT_EXTENSIONS,
  isValidBudgetAttachmentType,
  MAX_BUDGET_ATTACHMENT_SIZE,
  MAX_BUDGET_ATTACHMENTS,
} from "./utils";

interface IBudgetAttachmentsProps {
  files: IBudgetAttachmentFile[];
  budgetId?: string;
  allowUpload?: boolean;
  onFilesAdd?: (files: IBudgetAttachmentFile[]) => void;
  onFileRemove?: (index: number) => void;
}

export const BudgetAttachments = ({
  files,
  budgetId,
  allowUpload = false,
  onFilesAdd,
  onFileRemove,
}: IBudgetAttachmentsProps) => {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileSelect = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles || selectedFiles.length === 0 || !onFilesAdd) return;

      setErrorMessage("");
      const selectedFilesArray = Array.from(selectedFiles);
      const totalFiles = files.length + selectedFilesArray.length;

      if (totalFiles > MAX_BUDGET_ATTACHMENTS) {
        setErrorMessage(
          intl.formatMessage(
            { id: "intl-msg:budgets.attachments.maxFilesError" },
            { max: MAX_BUDGET_ATTACHMENTS, total: totalFiles }
          )
        );
        return;
      }

      const filesTooLarge: string[] = [];
      const invalidFileTypes: string[] = [];
      const validFiles: IBudgetAttachmentFile[] = [];

      for (const file of selectedFilesArray) {
        if (!isValidBudgetAttachmentType(file)) {
          invalidFileTypes.push(file.name);
          continue;
        }

        if (file.size > MAX_BUDGET_ATTACHMENT_SIZE) {
          filesTooLarge.push(file.name);
        } else {
          validFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            file,
          });
        }
      }

      if (invalidFileTypes.length > 0) {
        setErrorMessage(
          intl.formatMessage(
            { id: "intl-msg:budgets.attachments.invalidTypeError" },
            { files: invalidFileTypes.join(", ") }
          )
        );
      } else if (filesTooLarge.length > 0) {
        setErrorMessage(
          intl.formatMessage(
            { id: "intl-msg:budgets.attachments.maxSizeError" },
            { files: filesTooLarge.join(", ") }
          )
        );
      }

      if (validFiles.length > 0) {
        const remainingSlots = MAX_BUDGET_ATTACHMENTS - files.length;
        const filesToAdd = validFiles.slice(0, remainingSlots);
        onFilesAdd(filesToAdd);

        if (validFiles.length > remainingSlots) {
          setErrorMessage(
            intl.formatMessage(
              { id: "intl-msg:budgets.attachments.partialAddError" },
              { remaining: remainingSlots, max: MAX_BUDGET_ATTACHMENTS }
            )
          );
        }
      }
    },
    [files.length, intl, onFilesAdd]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  return (
    <div className="mb-6 px-6">
      <div className="font-bold mb-4">{intl.formatMessage({ id: "intl-msg:budgets.attachments.title" })}</div>

      {allowUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={ALLOWED_BUDGET_ATTACHMENT_EXTENSIONS.join(",")}
            onChange={handleInputChange}
          />
          {errorMessage && (
            <div className="mb-3 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-size-12">
              {errorMessage}
            </div>
          )}
          <section
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={handleDrop}
            aria-label={intl.formatMessage({ id: "intl-msg:budgets.attachments.uploadArea" })}
            className={`flex items-center justify-center p-4 mb-4 border-2 border-dashed rounded-md transition-colors ${
              isDragging ? "border-primary" : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <button
              type="button"
              className="cursor-pointer bg-transparent border-none p-0 flex flex-col items-center gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <img
                src="/assets/images/uploadFiles.svg"
                alt={intl.formatMessage({ id: "intl-msg:budgets.attachments.upload" })}
                className="w-10 h-10"
              />
              <span className="text-size-14 text-gray-600">
                {intl.formatMessage({ id: "intl-msg:budgets.attachments.dropHint" })}
              </span>
              <span className="text-size-12 text-gray-500 text-center">
                {intl.formatMessage({ id: "intl-msg:budgets.attachments.acceptedTypes" })}
              </span>
            </button>
          </section>
        </>
      )}

      {files.length === 0 ? (
        <div className="text-gray-500 italic">
          {intl.formatMessage({ id: "intl-msg:budgets.attachments.empty" })}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {files.map((file, index) => (
            <BudgetAttachmentCard
              key={`file-${file.name}-${index}-${file.size || ""}`}
              file={file}
              budgetId={budgetId}
              allowRemove={allowUpload && Boolean(file.file)}
              onRemove={() => onFileRemove?.(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export type { IBudgetAttachmentFile } from "./types";
